import React from 'react';
import {Box, IconButton, MenuItem} from '@mui/material';
import ZoneLeaflet from '../../../components/ZoneLeaflet';
import {CustomTextField} from '../../../components/CustomTextField';
import {CustomSelectAndChildren} from '../../../components/CustomSelectAndChildren';
import {MapArrowIcon, CloseIcon} from '../../../components/buttons/IconButtons';
import {DEFAULT_LATITUDE, DEFAULT_LONGITUDE} from '../../../Apis/functions';

/**
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : kpozos@ibl.mx
 *
 * Inputs of a building, shared by the create and the update modals: name,
 * status, the location search with its map and the coordinates the map fills.
 *
 * @param {string} idPrefix - Prefix given to every input id, `cbm` on create and `ubm` on update
 * @param {Object} item - Form model rendered by the inputs
 * @param {Function} handleChange - Generic change handler of the form model
 * @param {boolean} emptyFields - Marks the required inputs left empty
 * @param {Array} getListStatus - Status options of the select
 * @param {Array} employeeWorkStations - Zone options of the select
 * @param {Function} t - Localization function
 * @param {Function} handleCleanClick - Empties the location search input
 * @param {boolean} showOverlay - Overlay visibility over the map
 * @param {boolean} overlayHiding - Overlay fade out state
 * @param {Function} handleMouseEnter - Hides the overlay when the pointer enters the map
 * @param {Function} handleMouseLeave - Brings the overlay back while no location is picked
 * @param {Function} handleMapChange - Writes the coordinates picked on the map
 * @param {Array<number>} [latlng] - Coordinates the map opens on, the last ones used otherwise
 * @param {boolean} [showMapHint=true] - Renders the click on the map hint, only the create asks for it
 * @returns {JSX.Element} - React component with the building form inputs
 * @constructor
 */
const BuildingForm = ({
                          idPrefix,
                          item,
                          handleChange,
                          emptyFields,
                          getListStatus,
                          employeeWorkStations,
                          t,
                          handleCleanClick,
                          showOverlay,
                          overlayHiding,
                          handleMouseEnter,
                          handleMouseLeave,
                          handleMapChange,
                          latlng,
                          showMapHint = true
                      }) => {

    // Center of the map, the caller decides it and the last position is the fallback
    const mapCenter = latlng ?? [
        sessionStorage.getItem('latitude') === null
            ? DEFAULT_LATITUDE
            : sessionStorage.getItem('latitude'),
        sessionStorage.getItem('longitude') === null
            ? DEFAULT_LONGITUDE
            : sessionStorage.getItem('longitude'),
    ];

    return (
        <div className='row'>
            <div className='col-sm-12 col-md-6'>
                <CustomTextField trans={'name'}
                                 id={`txt_${idPrefix}_name`}
                                 value={item.name}
                                 name={"name"}
                                 onChange={handleChange}
                                 required={true}
                                 emptyFields={emptyFields}
                />
            </div>
            <div className='col-sm-12 col-md-6'>
                <CustomSelectAndChildren
                    id={`slt_${idPrefix}_status`}
                    name={"status"}
                    value={item.status}
                    key={item.status}
                    keyName="id"
                    onChange={handleChange}
                    trans={"status"}
                    label={`lbl_${idPrefix}_status`}
                    required={true}
                    emptyFields={emptyFields}
                    showNoSelectionOption={false}
                >
                    {getListStatus?.map((status) => (
                        <MenuItem key={status?.id} value={status?.id}>
                            {t(status?.name).toUpperCase()}
                        </MenuItem>
                    ))}
                </CustomSelectAndChildren>
            </div>
            <div className='col-12'>
                <CustomSelectAndChildren
                    id={`slt_${idPrefix}_zone`}
                    name={"zoneId"}
                    value={item.zoneId}
                    key={item.zoneId}
                    keyName="id"
                    onChange={handleChange}
                    trans={"zone"}
                    label={`lbl_${idPrefix}_zone`}
                    required={true}
                    emptyFields={emptyFields}
                    showNoSelectionOption={false}
                >
                    {employeeWorkStations?.map((zone) => (
                        <MenuItem key={zone?.id} value={zone?.id}>
                            {zone?.name}
                        </MenuItem>
                    ))}
                </CustomSelectAndChildren>
            </div>

            <Box className={"col-12"}>
                <CustomTextField
                    trans={'location'}
                    id={`txt_${idPrefix}_search`}
                    value={item.searchLocation}
                    onChange={handleChange}
                    name={"searchLocation"}
                    placeholder={'Search for an address or click on the map to drop a pin'}
                    InputProps={{
                        endAdornment: (
                            <IconButton onClick={handleCleanClick}>
                                <CloseIcon width={16} height={16}/>
                            </IconButton>
                        ),
                    }}
                />
            </Box>

            <div className='col-12 text-center input-padding-bottom'>
                <div className={"zone-map-wrapper"}
                     onMouseEnter={handleMouseEnter}
                     onMouseLeave={handleMouseLeave}
                >
                    {(showMapHint && showOverlay) &&
                        <div className={`map-overlay ${overlayHiding ? 'hide-overlay-map' : 'show-overlay-map'}`}>
                            <div className={""}>
                                <div className={"d-flex justify-content-center align-items-center mb-3"}>
                                    <span className={"map-arrow"}><MapArrowIcon className="icon-color-general-00"/></span>
                                </div>
                                <p className='legend ibl-font-w-600'>{t('click_location_in_the_map')}</p>
                            </div>
                        </div>
                    }
                    <ZoneLeaflet
                        setLatitude={(value) => handleMapChange('latitude', value)}
                        setLongitude={(value) => handleMapChange('longitude', value)}
                        searchLocation={item.searchLocation}
                        latlng={mapCenter}
                        classNameMap={"zone-map-container"}
                    />
                </div>
            </div>

            <div className='col-sm-12 col-md-6'>
                <CustomTextField trans={'latitude'}
                                 id={`txt_${idPrefix}_latitude`}
                                 value={item.latitude}
                                 name={"latitude"}
                                 onChange={handleChange}
                                 required={true}
                                 disabled={true}
                                 emptyFields={emptyFields}
                                 helperText={showMapHint ? 'click_location_in_the_map' : undefined}
                />
            </div>
            <div className='col-sm-12 col-md-6'>
                <CustomTextField trans={'longitude'}
                                 id={`txt_${idPrefix}_longitude`}
                                 value={item.longitude}
                                 disabled={true}
                                 name={"longitude"}
                                 onChange={handleChange}
                                 required={true}
                                 emptyFields={emptyFields}
                                 helperText={showMapHint ? 'click_location_in_the_map' : undefined}
                />
            </div>
        </div>
    );
};

export default BuildingForm;
